import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import _ from 'lodash';
import { StoryMediaQuery } from 'types/story';
import { AdminReducerState } from 'admin/reducers';
import { DEFAULT_MEDIA_QUERY_SIZES } from 'common/constants';
import { getMediaQueryBoundaries } from 'common/utils/story-media-query';

import Ruler from './Ruler';
import devicesConfig from './config';

import css from './Resizer.scss';

function getInitialPlatformWidth(props: { platform: Props['currentMediaQuery']; min: number; max: number }) {
	const toolsMargin = 112 * 2;

	switch (props.platform) {
		case 'desktop':
		case 'tablet':
			return `${Math.max(props.min, Math.min(window.innerWidth - toolsMargin, props.max))}px`;
		case 'mobile':
			return `${Math.min(375, props.max)}px`;
		default:
			return '';
	}
}

const SIDES = {
	LEFT: 'left',
	RIGHT: 'right',
};

type OwnState = any;

type State = OwnState & typeof Resizer.defaultProps;

type Props = {
	currentMediaQuery: AdminReducerState['cardEditor']['present']['currentMediaQuery'];
	mediaQuery: {
		defaultPlatform: StoryMediaQuery['defaultPlatform'];
		config: StoryMediaQuery['config'];
	};
	className?: string;
	onStartChangeSize?: () => void;
	onEndChangeSize?: (width: string) => void;
	onChangingSize?: (props: { width: number }) => void;
	onContainerClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
	initialWidth?: string;
	canvasWrapperClassName?: string;
};

export default class Resizer extends Component<Props, State> {
	static defaultProps = {
		children: null,
		className: '',
		initialWidth: '',
		onStartChangeSize: _.noop,
		onEndChangeSize: _.noop,
		onChangingSize: _.noop,
		onContainerClick: _.noop,
	};

	static DRAG_HANDLER_EVENT = 'Resizer/onDragHandlerMouseEvent';

	state = {
		isResizing: false,
		currentWidth: 0,
		updatedWidth: 0,
		error: null,
		resizeSide: null,
		isFullScreen: false,
	};

	containerRef = React.createRef<HTMLDivElement>();

	siteCanvasRef = React.createRef<HTMLDivElement>();

	rulerRef = React.createRef<HTMLDivElement>();

	startDragPoint = { posX: 0 };

	el = document.querySelector('.resizer') as HTMLElement;

	componentDidMount() {
		const { min, max } = this.getBoundaryValues(true);
		const width =
			this.props.initialWidth ||
			getInitialPlatformWidth({ platform: this.props.currentMediaQuery, min, max }) ||
			`${min}px`;

		if (this.siteCanvasRef.current) {
			this.setCanvasSize(width);
		}
		this.checkBoundaryValues();

		if (this.siteCanvasRefOffsetLeft === 0 && !this.state.isFullScreen) {
			this.setState({ isFullScreen: true });
		}

		window.addEventListener('resize', this.onResize);

		// Register global event to resize by CardEditor/ZoomSlider component
		window.addEventListener(Resizer.DRAG_HANDLER_EVENT, this.onWindowDragHandlerMouseEvent);
	}

	componentDidUpdate(prevProps: any) {
		const { min, max } = this.getBoundaryValues(true);
		const width = getInitialPlatformWidth({ platform: this.props.currentMediaQuery, min, max }) || `${min}px`;

		if (prevProps.currentMediaQuery !== this.props.currentMediaQuery) {
			this.setCanvasSize(width);
			if (this.props.onEndChangeSize) {
				this.props.onEndChangeSize(width);
			}
			this.checkBoundaryValues();
		}
	}

	componentWillUnmount() {
		window.removeEventListener(Resizer.DRAG_HANDLER_EVENT, this.onWindowDragHandlerMouseEvent);
		window.removeEventListener('resize', this.onResize);
		document.removeEventListener('mousemove', this.onDocumentMouseEvent);
		document.removeEventListener('mouseup', this.onDocumentMouseEvent);
	}

	get siteCanvasRefOffsetLeft() {
		return this.siteCanvasRef.current ? this.siteCanvasRef.current.getBoundingClientRect().left : 0;
	}

	setCanvasSize = (width: string | number) => {
		const canvas = this.siteCanvasRef.current;
		const w = typeof width === 'number' ? width : parseInt(width, 10);

		if (!canvas) return;

		canvas.style.width = `${w}px`;

		Object.values(DEFAULT_MEDIA_QUERY_SIZES)
			.sort((a, b) => (a.min || 0) - (b.min || 0))
			.forEach(({ min, ratio }) => {
				if (+w >= (min || 0)) {
					canvas.style.height = `${+w / ratio}px`;
				}
			});
	};

	/**
	 * pureValue param need for getting pure min or max without checking
	 * if they fit in containerWidth
	 * @param {Boolean} pureValues
	 */
	getBoundaryValues = (pureValues = false) => {
		const { currentMediaQuery, mediaQuery } = this.props;
		const containerWidth = this.containerRef.current?.offsetWidth;
		const boundaries = getMediaQueryBoundaries({ mediaQuery, currentMediaQuery, containerWidth });

		return {
			min: pureValues ? boundaries.min : Math.min(boundaries.min, boundaries.containerWidth),
			max: pureValues ? boundaries.max : Math.min(boundaries.max, boundaries.containerWidth),
			isWidest: boundaries.isWidest,
			containerWidth: boundaries.containerWidth,
		};
	};

	checkBoundaryValues = () => {
		const containerWidth = this.containerRef.current?.offsetWidth || 0;
		const { min } = this.getBoundaryValues(true);

		if (min > containerWidth) {
			this.setState({
				error: 'Your browser is too small. Resize your window to fit this media query',
			});
		} else {
			this.setState({ error: null });
		}
	};

	onResize = () => {
		if (!this.siteCanvasRef.current || !this.containerRef.current) {
			return;
		}

		const canvasWidth = this.siteCanvasRef.current.offsetWidth;
		const containerWidth = this.containerRef.current.offsetWidth;
		const { error } = this.state;

		if (canvasWidth > containerWidth && !error) {
			this.setCanvasSize(this.containerRef.current.offsetWidth);
			if (this.props.onEndChangeSize) {
				this.props.onEndChangeSize(this.siteCanvasRef.current.style.width);
			}
		}

		this.checkBoundaryValues();
	};

	onDocumentMouseEvent = e => {
		const { currentWidth, resizeSide } = this.state;
		const { onEndChangeSize, onChangingSize } = this.props;
		const { min, max } = this.getBoundaryValues();
		const { points } = devicesConfig;

		let pbTargetPosX = 0;
		let targetWidth: number;
		let pageX = 0;

		switch (e.type) {
			case 'mousemove':
			case 'mouseup':
				pageX = _.get(e, 'pageX');
				pbTargetPosX = (pageX - this.startDragPoint.posX) * 2;

				if (resizeSide === SIDES.LEFT) {
					targetWidth = Math.max(min, Math.min(max, currentWidth - pbTargetPosX));
				} else {
					targetWidth = Math.max(min, Math.min(max, currentWidth + pbTargetPosX));
				}

				_.forEach(points, (devices, point) => {
					const inRange = _.inRange(point, min, max + 1);
					const isShouldSnap = Math.abs(parseFloat(point) - targetWidth) < 5 && inRange;
					if (isShouldSnap) {
						targetWidth = parseFloat(point);
					}
				});

				if (this.siteCanvasRef.current) {
					this.setCanvasSize(targetWidth);
				}

				this.setState({ updatedWidth: targetWidth }, () => {
					if (this.rulerRef.current) {
						this.rulerRef.current.style.left = `${this.siteCanvasRefOffsetLeft}px`;
					}

					if (this.siteCanvasRefOffsetLeft === 0 && !this.state.isFullScreen) {
						this.setState({ isFullScreen: true });
					} else if (this.siteCanvasRefOffsetLeft !== 0 && this.state.isFullScreen) {
						this.setState({ isFullScreen: false });
					}

					if (onChangingSize) {
						onChangingSize({ width: this.state.updatedWidth });
					}
				});

				if (e.type === 'mouseup') {
					this.setState({ isResizing: false, resizeSide: null }, () => {
						if (this.rulerRef.current) {
							this.rulerRef.current.style.left = `${this.siteCanvasRefOffsetLeft}px`;
						}

						if (this.siteCanvasRefOffsetLeft === 0 && !this.state.isFullScreen) {
							this.setState({ isFullScreen: true });
						} else if (this.siteCanvasRefOffsetLeft !== 0 && this.state.isFullScreen) {
							this.setState({ isFullScreen: false });
						}

						if (onEndChangeSize && this.siteCanvasRef.current) {
							onEndChangeSize(this.siteCanvasRef.current.style.width);
						}
					});

					this.startDragPoint.posX = pbTargetPosX;

					document.removeEventListener('mousemove', this.onDocumentMouseEvent);
					document.removeEventListener('mouseup', this.onDocumentMouseEvent);
				}
				break;

			default:
				break;
		}
	};

	onDragHandlerMouseEvent = e => {
		const { onStartChangeSize } = this.props;
		const { side } = e.currentTarget.dataset;
		let pageX;

		switch (e.type) {
			case 'mousedown':
				document.addEventListener('mousemove', this.onDocumentMouseEvent);
				document.addEventListener('mouseup', this.onDocumentMouseEvent);

				pageX = _.get(e, 'pageX');

				this.setState(
					{
						isResizing: true,
						resizeSide: side,
						updatedWidth: this.siteCanvasRef.current?.offsetWidth || 0,
						currentWidth: Math.min(
							this.containerRef.current?.offsetWidth || 0,
							this.siteCanvasRef.current?.offsetWidth || 0
						),
					},
					() => {
						if (this.rulerRef.current) {
							this.rulerRef.current.style.left = `${this.siteCanvasRefOffsetLeft}px`;
						}

						if (this.siteCanvasRefOffsetLeft === 0 && !this.state.isFullScreen) {
							this.setState({ isFullScreen: true });
						} else if (this.siteCanvasRefOffsetLeft !== 0 && this.state.isFullScreen) {
							this.setState({ isFullScreen: false });
						}

						this.startDragPoint.posX = pageX;
						if (onStartChangeSize) {
							onStartChangeSize();
						}
					}
				);
				break;

			default:
				break;
		}
	};

	onWindowDragHandlerMouseEvent = e => {
		this.onDragHandlerMouseEvent({
			type: 'mousedown',
			pageX: e.detail.pageX,
			currentTarget: {
				dataset: SIDES.LEFT,
			},
		});
	};

	renderToolTips = () => {
		const { updatedWidth } = this.state;
		const vpWidth = window.innerWidth;
		const devices = devicesConfig.points[updatedWidth];
		const left = vpWidth * 0.5 + updatedWidth * 0.5;

		return (
			<>
				<div className={css.tooltip} style={{ left: `${left}px` }}>
					{`${updatedWidth}px`}
				</div>
				<div className={css.devicesTooltip} style={{ left: `${left}px` }}>
					{_.map(devices, device => (
						<div className={css.device} key={device}>
							{device}
						</div>
					))}
				</div>
			</>
		);
	};

	render() {
		const { children, mediaQuery, className, canvasWrapperClassName } = this.props;
		const { error, isResizing, isFullScreen } = this.state;

		return (
			children && (
				<div
					ref={this.containerRef}
					className={classNames(css.resizer, className, {
						[css.error]: error,
						[css.resizing]: isResizing,
					})}
					onClick={this.props.onContainerClick}
				>
					{error && <div className={css.errorMessage}>{error}</div>}
					<div ref={this.siteCanvasRef} className={classNames(css.siteCanvas, canvasWrapperClassName)}>
						<div
							className={classNames(css.dragHandler, css.left)}
							onMouseDown={this.onDragHandlerMouseEvent}
							data-side={SIDES.LEFT}
						/>
						<div
							className={classNames(css.dragHandler, css.right)}
							onMouseDown={this.onDragHandlerMouseEvent}
							data-side={SIDES.RIGHT}
						/>
						{children}
					</div>
					{isResizing && ReactDOM.createPortal(this.renderToolTips(), this.el)}
					{isResizing && (
						<Ruler
							el={this.el}
							currentMediaQuery={this.props.currentMediaQuery}
							mediaQuery={mediaQuery}
							isFullScreen={isFullScreen}
							cbRef={this.rulerRef}
							className={css.ruler}
						/>
					)}
				</div>
			)
		);
	}
}
